/* CRM v3 search — the pure module behind the ⌘K palette and
   `tools/crm/dossier.mjs --q`.

   No React, no Firestore, no DOM, no imports: this file is loaded both by Vite
   (browser) and by plain `node` from tools/, the same way tools/crm/fixlib.mjs
   loads src/lib/constants.js. Keep it dependency-free.

   Two calls:

     const index = buildIndex({ contacts, clients, jobs, quotes, interactions });
     const hits  = search(index, "0142 monument", { limit: 8 });

   `search` returns a flat, ordered array of results. Each result names the
   field that matched and the character ranges inside it that matched, so the
   caller can render <mark> without re-doing any matching:

     { kind, id, title, subtitle, matchedField, matchedText, spans, score, at, record }

   Ranking, per match, highest first (see matchTier):

     4  the whole field equals the term          "hbo"        → clientCode "hbo"
     3  the field starts with the term           "art"        → name "Art Camp"
     2  a word inside the field starts with it   "monu"       → notes "…the monument base…"
     1  the term appears anywhere                "rtne"       → "Partnerships"

   A record's score is the sum of its best match per query word. EVERY word of a
   multi-word query must match somewhere on the record, which is what makes the
   mockup's "0142 monument" query pick out one phone number and one note rather
   than everything containing either. Ties break on `at`, newest first, then id,
   so the order is stable and identical in the browser and in node. */

/* ── group order ──────────────────────────────────────────────────────────
   The palette renders groups in this order and skips the empty ones (the
   mockup's example query has no job hits, so Jobs is simply absent). */
export const KIND_ORDER = ["contact", "interaction", "quote", "job", "client"];

export const KIND_LABELS = {
  contact: "Contacts",
  interaction: "Messages",
  quote: "Quotes",
  job: "Jobs",
  client: "Clients",
};

/* Two-letter badge at the left of a palette row (mockup .pr .ic). */
export const KIND_BADGES = {
  contact: "CT",
  client: "CL",
  job: "J",
  quote: "Q",
  interaction: "✉",
};

const DEFAULT_LIMIT = 8;

/* ── normalization ────────────────────────────────────────────────────────

   Everything is matched against a lowercased, accent-stripped, whitespace-
   collapsed copy of the field. Highlighting has to point back into the
   ORIGINAL string, so normalize() also returns a map from normalized index to
   raw index — a plain `.normalize("NFD")` would shift every offset after the
   first accented character and put the <mark> in the wrong place. */

const COMBINING = /[\u0300-\u036f]/g;

/* A string whose normalized form is its lowercased self, character for
   character: plain printable ASCII, single-spaced, no leading or trailing
   space. Almost every real field is one, and for those the normalized index IS
   the raw index, so no map has to be built or carried. That fast path is what
   keeps buildIndex inside the main thread's budget with thousands of records. */
const NEEDS_MAP = /[^\x20-\x7E]| {2}|^ | $/;

/** { norm, map }. `map` is null when the normalized index equals the raw index;
 *  otherwise map[i] is the raw index of norm[i]. Read it through rawIndex() —
 *  a plain `.normalize("NFD")` would shift every offset after the first
 *  accented character and put the <mark> in the wrong place. */
export function normalize(raw) {
  const s = String(raw ?? "");
  if (!NEEDS_MAP.test(s)) {
    const lower = s.toLowerCase();
    // A handful of characters change length when lowercased (ẞ becomes ss);
    // those fall through to the mapped path rather than skewing every offset.
    if (lower.length === s.length) return { norm: lower, map: null };
  }
  let norm = "";
  const map = [];
  let pendingSpace = -1;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      // Collapse runs of whitespace, and never emit a leading one. The single
      // space that stands in for the run maps to the FIRST raw whitespace
      // character, so a span that ends on it still lands inside the field.
      if (norm.length && pendingSpace < 0) pendingSpace = i;
      continue;
    }
    const base = ch.normalize("NFD").replace(COMBINING, "").toLowerCase();
    if (!base) continue; // a bare combining mark folds into the previous letter
    if (pendingSpace >= 0) { norm += " "; map.push(pendingSpace); pendingSpace = -1; }
    for (const c of base) { norm += c; map.push(i); }
  }
  // Trailing whitespace was never emitted, so norm is already trimmed.
  map.push(s.length);
  return { norm, map };
}

/** The raw offset of normalized index `i`, for either map shape. */
export function rawIndex(map, i) {
  return map ? map[i] : i;
}

/** Digits only, with an index map. Used for phone fields and for queries that
 *  are all digits once punctuation is stripped. */
export function digitsOf(raw) {
  const s = String(raw ?? "");
  let norm = "";
  const map = [];
  for (let i = 0; i < s.length; i++) {
    if (s[i] >= "0" && s[i] <= "9") { norm += s[i]; map.push(i); }
  }
  map.push(s.length);
  return { norm, map };
}

/** Query words. Punctuation that is not part of a word is dropped, but a term
 *  that is a phone fragment keeps its digits (see termOf). */
export function tokenize(query) {
  const { norm } = normalize(query);
  return norm.split(" ").map(termOf).filter(Boolean);
}

/* A query word, with the digits-only form it should also be tried as.
   "555-0142" → { text: "555-0142", digits: "5550142" }; "monument" has no
   digits form, so a phone field is never searched for it. */
function termOf(word) {
  const text = word.replace(/^[^\p{L}\p{N}@.+_'-]+|[^\p{L}\p{N}@.+_'-]+$/gu, "");
  if (!text) return null;
  const digits = text.replace(/\D+/g, "");
  return { text, digits: digits.length >= 2 && /^[\d\s()+.-]+$/.test(text) ? digits : "" };
}

/* ── matching ─────────────────────────────────────────────────────────────

   Returns 0 (no match) or a tier 1-4. `at` is where the winning match starts
   in the normalized haystack, so the caller can map it back to raw offsets. */
export function matchTier(hay, term) {
  if (!hay || !term) return { tier: 0, at: -1 };
  // One native indexOf decides the common case — no match — before any of the
  // per-character work below. With ~10,000 indexed records and every field
  // scanned per keystroke, this short-circuit is the difference between a
  // palette that keeps up with typing and one that does not.
  const idx = hay.indexOf(term);
  if (idx < 0) return { tier: 0, at: -1 };
  if (idx === 0) return { tier: hay.length === term.length ? 4 : 3, at: 0 };
  // A word inside the field starting with the term. Word boundary = a space,
  // or one of the separators that show up inside emails, handles and codes.
  for (let i = idx; i >= 0; i = hay.indexOf(term, i + 1)) {
    if (SEPARATORS.has(hay.charCodeAt(i - 1))) return { tier: 2, at: i };
  }
  return { tier: 1, at: idx };
}

/* Character codes for: space " ' ( ) , - . / : ; @ _ */
const SEPARATORS = new Set([32, 34, 39, 40, 41, 44, 45, 46, 47, 58, 59, 64, 95]);

/* ── the index ────────────────────────────────────────────────────────────

   One entry per record. `fields` are pre-normalized once at build time, which
   is the whole reason typing stays instant over thousands of contacts. */

function field(label, raw, weight, { phone = false } = {}) {
  const value = typeof raw === "string" ? raw : raw == null ? "" : String(raw);
  if (!value.trim()) return null;
  const f = { label, raw: value, weight, ...normalize(value) };
  if (phone) f.digits = digitsOf(value);
  return f;
}

const arr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const str = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

/* `at` is only ever compared to other `at`s, so a plain sortable string is
   enough. Firestore Timestamps and Dates both reach the client. */
export function isoOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.toDate === "function") { try { return value.toDate().toISOString(); } catch { return ""; } }
  // new Date("not a date") is truthy and throws from toISOString(). One bad
  // captureDate in one job would otherwise take the whole index down, and with
  // it the palette.
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  return "";
}

/** Latest date a job covers: a session date if there are sessions, else the
 *  root captureDate, else when it was quoted or created. Mirrors jobDate() in
 *  tools/crm/lib.mjs so the two paths tie-break identically. */
function jobDate(j) {
  const ds = [isoOf(j?.captureDate), ...arr(j?.sessions).map((s) => isoOf(s?.date))].filter(Boolean).sort();
  return ds[ds.length - 1] || isoOf(j?.quoteDate) || isoOf(j?.createdAt) || "";
}

function quoteDate(q) {
  return isoOf(q?.quoteDate) || isoOf(q?.savedAt) || isoOf(q?.createdAt) || "";
}

/* Quotes carry no quoteNumber in live data — the human handle is the filename
   the builder produced (tools/crm/dossier.mjs makes the same choice). */
function quoteHandle(q) {
  return str(q?.quoteNumber) || str(q?.filename) || str(q?.id);
}

function lineItemText(q) {
  const items = [
    ...arr(q?.lineItems),
    ...arr(q?.lineGroups).flatMap((g) => arr(g?.lineItems ?? g?.items)),
    ...arr(q?.sections).flatMap((s) => arr(s?.lineItems ?? s?.items)),
  ];
  return items
    .map((li) => str(li?.description ?? li?.label ?? li?.name))
    .filter(Boolean)
    .join(" · ");
}

function moneyText(n) {
  const v = typeof n === "number" ? n : Number(String(n ?? "").replace(/[$,]/g, ""));
  if (!Number.isFinite(v) || v === 0) return "";
  return `${Math.round(v)} $${Math.round(v).toLocaleString("en-US")}`;
}

function contactEntry(c) {
  const emails = [...new Set([...arr(c.emails), c.email].map(str).filter(Boolean))];
  const fields = [
    field("name", c.name, 6),
    ...emails.map((e) => field("email", e, 5)),
    field("phone", c.phone, 5, { phone: true }),
    field("company", c.company, 4),
    ...arr(c.tags).map((t) => field("tag", t, 3)),
    field("notes", c.notes, 2),
    field("linkedin", c.social?.linkedin?.url, 2),
  ];
  return {
    kind: "contact",
    id: str(c.id || c._docId),
    title: str(c.name) || emails[0] || str(c.id),
    subtitle: str(c.company),
    at: isoOf(c.lastContact) || isoOf(c.firstContact),
    fields: fields.filter(Boolean),
    record: c,
  };
}

function clientEntry(c) {
  const fields = [
    field("name", c.name, 6),
    field("code", c.clientCode, 5),
    field("domain", c.orgDomain, 5),
    ...arr(c.tags).map((t) => field("tag", t, 3)),
    field("notes", c.notes, 2),
    field("address", typeof c.billingAddress === "string"
      ? c.billingAddress
      : Object.values(c.billingAddress || {}).filter((v) => typeof v === "string").join(" "), 2),
  ];
  return {
    kind: "client",
    id: str(c.id || c._docId),
    title: str(c.name) || str(c.clientCode) || str(c.id),
    subtitle: str(c.orgDomain),
    at: isoOf(c.lastContact) || isoOf(c.firstContact),
    fields: fields.filter(Boolean),
    record: c,
  };
}

function jobEntry(j) {
  const codes = [...new Set([str(j.jobId), str(j.id), str(j._docId)].filter(Boolean))];
  const fields = [
    ...codes.map((code) => field("job code", code, 6)),
    field("client", j.clientName, 5),
    field("contact", j.contactName, 5),
    field("subject", j.subject || j.title || j.project, 4),
    field("date", isoOf(j.captureDate).slice(0, 10), 3),
  ];
  return {
    kind: "job",
    id: str(j.id || j._docId),
    title: codes[0] || str(j.id),
    subtitle: str(j.clientName),
    at: jobDate(j),
    fields: fields.filter(Boolean),
    record: j,
  };
}

function quoteEntry(q) {
  const fields = [
    field("handle", quoteHandle(q), 6),
    field("client", q.clientName, 5),
    field("contact", q.contactName, 5),
    field("line items", lineItemText(q), 3),
    field("total", moneyText(q.grandTotal ?? q.total ?? q.subtotal), 3),
  ];
  return {
    kind: "quote",
    id: str(q.id || q._docId),
    title: quoteHandle(q),
    subtitle: str(q.clientName),
    at: quoteDate(q),
    fields: fields.filter(Boolean),
    record: q,
  };
}

function interactionEntry(r) {
  const isMessage = r.type === "message";
  const fields = isMessage
    ? [
      field("subject", r.subject, 6),
      field("from", r.from, 4),
      field("to", arr(r.to).map(str).join(", "), 4),
      field("body", r.body, 2),
    ]
    : [field("summary", r.summary, 4)];
  return {
    kind: "interaction",
    id: str(r.id || r._docId),
    title: str(isMessage ? r.subject : r.summary) || str(r.type || "record"),
    subtitle: str(isMessage ? r.from : r.type),
    at: isoOf(r.at) || isoOf(r.date),
    fields: fields.filter(Boolean),
    record: r,
  };
}

/** Build the in-memory index. Every collection is optional; the palette builds
 *  once without `interactions` and again once the admin-only collection has
 *  loaded. Soft-deleted records are skipped. */
export function buildIndex({ contacts, clients, jobs, quotes, interactions } = {}) {
  const live = (r) => r && !r.deletedAt;
  const entries = [
    ...arr(contacts).filter(live).map(contactEntry),
    ...arr(clients).filter(live).map(clientEntry),
    ...arr(jobs).filter(live).map(jobEntry),
    ...arr(quotes).filter(live).map(quoteEntry),
    ...arr(interactions).filter(live).map(interactionEntry),
  ].filter((e) => e.id && e.fields.length);
  return { entries, size: entries.length };
}

/** Concatenate indexes built separately. The palette builds one index over the
 *  collections it already has and a second over `interactions` when that
 *  admin-only collection finally loads, then merges — so the arrival of
 *  messages never re-normalizes every contact, client, job and quote again. */
export function mergeIndexes(...indexes) {
  const entries = [];
  for (const idx of indexes) if (idx && idx.entries) entries.push(...idx.entries);
  return { entries, size: entries.length };
}

/* ── search ───────────────────────────────────────────────────────────────*/

/* Best match of one term against one field, as { tier, start, end } in RAW
   coordinates, or null. */
function fieldMatch(f, term) {
  let best = null;
  const text = matchTier(f.norm, term.text);
  if (text.tier) {
    // The end offset is the LAST matched character's raw index plus one, never
    // map[at + length]: where the next normalized character is the space that
    // stands in for a run of whitespace, that lookup would swallow the run.
    best = {
      tier: text.tier,
      start: rawIndex(f.map, text.at),
      end: rawIndex(f.map, text.at + term.text.length - 1) + 1,
    };
  }
  if (term.digits && f.digits && f.digits.norm) {
    const d = matchTier(f.digits.norm, term.digits);
    // A digit run inside a formatted number is a deliberate hit, not an
    // accidental substring: "0142" should find "+1 917 555 0142". Lift a bare
    // containment to the same tier a word prefix gets.
    const tier = d.tier === 1 ? 2 : d.tier;
    if (d.tier && (!best || tier > best.tier)) {
      best = { tier, start: f.digits.map[d.at], end: f.digits.map[d.at + term.digits.length - 1] + 1 };
    }
  }
  return best;
}

/** Search the index. Returns a flat array ordered by KIND_ORDER, then score,
 *  then newest, capped at `limit` results per kind. */
export function search(index, query, { limit = DEFAULT_LIMIT, kinds } = {}) {
  const terms = tokenize(query);
  if (!terms.length || !index || !index.entries) return [];
  const wanted = kinds && kinds.length ? new Set(kinds) : null;

  const hits = [];
  for (const entry of index.entries) {
    if (wanted && !wanted.has(entry.kind)) continue;

    let score = 0;
    let complete = true;
    let bestOverall = null; // { field, tier, weight, spans }
    const perField = new Map(); // field -> spans, so a multi-word hit in one field marks both words

    for (const term of terms) {
      let bestForTerm = null;
      for (const f of entry.fields) {
        const m = fieldMatch(f, term);
        if (!m) continue;
        const s = m.tier * 100 + f.weight;
        if (!bestForTerm || s > bestForTerm.score) bestForTerm = { f, m, score: s };
      }
      if (!bestForTerm) { complete = false; break; }
      score += bestForTerm.score;
      const spans = perField.get(bestForTerm.f) || [];
      spans.push([bestForTerm.m.start, bestForTerm.m.end]);
      perField.set(bestForTerm.f, spans);
      if (!bestOverall || bestForTerm.score > bestOverall.score) bestOverall = bestForTerm;
    }
    if (!complete || !bestOverall) continue;

    const f = bestOverall.f;
    hits.push({
      kind: entry.kind,
      id: entry.id,
      title: entry.title,
      subtitle: entry.subtitle,
      matchedField: f.label,
      matchedText: f.raw,
      spans: mergeSpans(perField.get(f) || []),
      score,
      at: entry.at,
      record: entry.record,
    });
  }

  hits.sort((a, b) => {
    const ka = KIND_ORDER.indexOf(a.kind);
    const kb = KIND_ORDER.indexOf(b.kind);
    if (ka !== kb) return ka - kb;
    if (a.score !== b.score) return b.score - a.score;
    if (a.at !== b.at) return a.at < b.at ? 1 : -1; // newest first
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const count = new Map();
  return hits.filter((h) => {
    const n = (count.get(h.kind) || 0) + 1;
    count.set(h.kind, n);
    return n <= limit;
  });
}

function mergeSpans(spans) {
  const sorted = [...spans].filter((s) => s && s[1] > s[0]).sort((a, b) => a[0] - b[0]);
  const out = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && s[0] <= last[1]) last[1] = Math.max(last[1], s[1]);
    else out.push([s[0], s[1]]);
  }
  return out;
}

/** Results split into display groups, empty groups dropped, in KIND_ORDER. */
export function groupResults(results) {
  return KIND_ORDER
    .map((kind) => ({ kind, label: KIND_LABELS[kind], results: (results || []).filter((r) => r.kind === kind) }))
    .filter((g) => g.results.length);
}

/** The matched field cut down to a window around the first highlight, for long
 *  fields like a message body. Returns { text, spans, leading, trailing } with
 *  spans rebased onto `text`; the caller adds the ellipses. */
export function snippet(result, radius = 44) {
  const text = str(result?.matchedText);
  const spans = result?.spans || [];
  if (!spans.length || text.length <= radius * 2) return { text, spans, leading: false, trailing: false };
  /* The window sits around the FIRST hit, not around all of them. Two hits at
     either end of a long email body used to widen the window to the whole
     body, so the row printed hundreds of characters and the palette's single
     line showed nothing but the middle of a message. Later hits are kept when
     they happen to fall inside the window and clipped away when they do not. */
  const first = spans[0];
  const start = Math.max(0, first[0] - radius);
  const end = Math.min(text.length, first[1] + radius);
  return {
    text: text.slice(start, end),
    spans: spans
      .map(([s, e]) => [s - start, e - start])
      .filter(([s, e]) => e > 0 && s < end - start)
      .map(([s, e]) => [Math.max(0, s), Math.min(end - start, e)]),
    leading: start > 0,
    trailing: end < text.length,
  };
}

/** Split `text` into alternating plain / highlighted pieces. Both the React
 *  palette and the node markdown renderer walk this, so a highlight can never
 *  land in one place in the browser and another in a dossier. */
export function highlightParts(text, spans) {
  const s = str(text);
  const parts = [];
  let at = 0;
  for (const [start, end] of spans || []) {
    const a = Math.max(0, Math.min(s.length, start));
    const b = Math.max(a, Math.min(s.length, end));
    if (b <= at) continue;
    if (a > at) parts.push({ text: s.slice(at, a), hit: false });
    parts.push({ text: s.slice(a, b), hit: true });
    at = b;
  }
  if (at < s.length) parts.push({ text: s.slice(at), hit: false });
  return parts.filter((p) => p.text);
}
